import { ChangeEvent, RefObject, createRef, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { endOfDay, startOfDay } from 'date-fns';

import { KeyboardArrowUp as KeyboardArrowUpIcon, SettingsOutlined as SettingsIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';

import { DetailedRoute } from '@/generated/model/detailedRoute';
import { DateRangeSelect } from '@/components/common/date/DateRangeSelect';

import { BottomSheet } from '@/components/common/BottomSheet';
import { HeaderNameAndIcons } from '@/components/common/HeaderNameAndIcons';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { getRoutesWithPositions, getTargetRoutes } from '@/generated/client/routes/routes';
import { RoutesHeader } from './RoutesHeader';
import { RoutesDateSelect } from './RoutesDateSelect';
import { RoutesSummary } from './RoutesSummary';
import { RouteGrouper } from './RouteGrouper';
import { TargetNameAndDescription, targetNameAndDescriptionStyle } from '@/components/common/TargetNameAndDescription';
import TargetSelectorOpenerButton from '@/components/common/TargetSelectorOpenerButton';

import { Popup, SingleTargetSelector } from '@/components';
import { ctxPluto } from '@/contexts/PlutoContext';
import { ctxPopup } from '@/contexts/PopupContext';
import { ctxRoutes } from '@/contexts/RoutesCtx.tsx';
import { MinimalTarget, RouteMetadata } from '@/generated/model';
import { RoutesSummaryHeader } from './RoutesSummaryHeader';
import { useRouteListScroll } from '@/hooks/useRouteListScroll';
import { useNotificationStore } from '@/stores/notificationstore';
import { SelectedRoute, TabValue } from '@/types';
import { fulldate } from '@/utilities/dateUtils';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';
import RouteList from './RouteList';

// eslint-disable-next-line react-refresh/only-export-components
export function convertRouteToSelected(route: DetailedRoute, selectedTarget: MinimalTarget): SelectedRoute {
  return {
    id: route.id,
    target: selectedTarget,
    fromTimeMicros: route.startPosition.timeMicros,
    toTimeMicros: route.stopPosition.timeMicros,
  } satisfies SelectedRoute;
}

function date2micros(d: Date) {
  return d.getTime() * 1000;
}

function convertRouteListToSelected(
  routes: DetailedRoute[] | undefined,
  selectedTarget: MinimalTarget | undefined
): SelectedRoute[] {
  if (!routes || !selectedTarget) return [];
  return routes.map((r) => convertRouteToSelected(r, selectedTarget));
}

function stripYear(d: string) {
  if (!/^20\d\d-/.test(d)) return d;
  return d.substring(5);
}

function QuickNav({ label, current, onClickScroll }: { label: string; current: boolean; onClickScroll: () => void }) {
  return (
    <Chip
      sx={{
        display: 'flex',
        marginBottom: '2px',
        color: current ? 'var(--mui-palette-primary-contrast-text)' : 'var(--mui-palette-text-primary)',
        backgroundColor: current ? 'var(--mui-palette-primary-main)' : 'var(--mui-palette-action-selected)',
      }}
      key={label + 'nav'}
      onClick={onClickScroll}
      label={label}
    ></Chip>
  );
}

export function RoutesResultHeader() {
  const routeCtx = useContext(ctxRoutes);
  const { t } = useTranslation();
  const [isRotated, setIsRotated] = useState(false);
  const [show, setShow] = useState(false);
  const showRef = useRef(false);

  useEffect(() => {
    showRef.current =
      routeCtx?.showSummaryTooltip &&
      routeCtx?.highlightedRoutes?.length !== 0 &&
      JSON.parse(sessionStorage.getItem('showRoutesSummaryTooltip') ?? 'true');
  }, [routeCtx?.showSummaryTooltip, routeCtx?.highlightedRoutes?.length]);

  useEffect(() => {
    if (showRef.current) {
      if (!show) {
        if (showRef.current) {
          setShow(showRef.current);
        }
      }
    } else setShow(false);
  }, [routeCtx?.showSummaryTooltip, routeCtx?.highlightedRoutes?.length]);

  function handlePoiNamesSelect(event: ChangeEvent<HTMLInputElement>) {
    const checked = event.target.checked;
    routeCtx?.setPoiNamesEnabled(checked);
  }

  function onAccept(newFrom: Date, newTo: Date) {
    if (!newFrom || !newTo) return;
    const from = startOfDay(newFrom);
    const to = endOfDay(newTo);
    routeCtx?.setSelectedDates({ from, to });
  }

  function RoutesResultHeader() {
    if (!routeCtx) return;
    return (
      <>
        <DateRangeSelect
          setSelectedDates={onAccept}
          currentValue={routeCtx.selectedDates || { from: null, to: null }}
        />
      </>
    );
  }

  function handleChange(_event: unknown, expanded: boolean) {
    setIsRotated(expanded);
  }

  if (!routeCtx || !routeCtx.selectedTarget) return;

  return (
    <div
      className={`route-result-header ${routeCtx.routesLoading ? 'disabled' : ''}`}
      data-qa-element-id="route_result_header"
    >
      {isPhoneOrHighScreen() ? (
        <HeaderNameAndIcons target={routeCtx.selectedTarget} onClose={() => routeCtx.setIsBottomSheetOpen(false)} />
      ) : (
        <TargetNameAndDescription
          targetName={routeCtx.selectedTarget?.name}
          targetDescription={routeCtx.selectedTarget?.description}
          styleClassName={targetNameAndDescriptionStyle(undefined, undefined)}
        />
      )}
      {!isPhoneOrHighScreen() && <RoutesResultHeader />}
      <Accordion
        onChange={handleChange}
        className="route-settings-accordion"
        data-qa-element-id="route_settings_dropdown"
        disableGutters
      >
        <AccordionSummary
          id="route-settings"
          expandIcon={
            <KeyboardArrowUpIcon
              sx={{
                transform: 'rotate(180deg)',
                transition: 'transform 0.3s ease',
              }}
              data-qa-element-id="open_accordion_icon"
            />
          }
        >
          <SettingsIcon
            id="route-settings-icon"
            data-qa-element-id="route_settings_icon"
            sx={{
              transform: isRotated ? 'rotate(120deg)' : 'rotate(0deg)',
              transition: 'transform 0.8s ease',
            }}
          />
          <Typography variant="subtitle2" sx={{ py: 1 }}>
            {t('general.settings')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column' }} data-qa-element-id="route_settings_panel">
          <FormControlLabel
            control={
              <Switch
                onChange={handlePoiNamesSelect}
                checked={routeCtx.poiNamesEnabled}
                data-qa-element-id="poi_label_switch"
              />
            }
            label={t('routes.onlyPoiAddresses')}
          />
        </AccordionDetails>
      </Accordion>
      {show && (
        <div className="route-summary-highlight-tooltip" data-qa-element-id="deselect_all_routes_tooltip">
          {t('routes.routeSummaryTooltip')}
        </div>
      )}
      {isPhoneOrHighScreen() && <RoutesSummaryHeader />}
    </div>
  );
}

function RoutesList({ topRef }: { topRef: RefObject<HTMLDivElement | null> }) {
  const plutoCtx = useContext(ctxPluto);
  const routeCtx = useContext(ctxRoutes);
  const popupCtx = useContext(ctxPopup);
  const [selectAllChecked, setSelectAllChecked] = useState(
    plutoCtx?.selectedRoutes.length === routeCtx?.routes?.length
  );
  const { t, i18n } = useTranslation();
  const showNotification = useNotificationStore((state) => state.showNotification);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const refs = useRef<Map<string, RefObject<HTMLDivElement | null>>>(new Map());
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [activeSection, setActiveSection] = useState<HTMLDivElement | null>(topRef.current);
  const [showTooltip, setShowTooltip] = useState<boolean>(
    JSON.parse(sessionStorage.getItem('showRoutesListTooltip') ?? 'true')
  );
  const { registerRef, scrollTo } = useRouteListScroll();

  useEffect(() => {
    if (!routeCtx?.hasBeenInitialized) {
      handleSelectAll();
      routeCtx?.setHasBeenInitialized(true);
    }
  }, []);

  useEffect(() => {
    setSelectAllChecked(plutoCtx?.selectedRoutes.length === routeCtx?.routes?.length);
  }, [plutoCtx?.selectedRoutes, routeCtx?.routes]);

  useEffect(() => {
    const controller = new AbortController();
    void setResult(controller.signal);
    if (routeCtx?.selectedDates || routeCtx?.selectedTarget) {
      routeCtx.setAdjustMap(true);
    }
    return () => controller.abort();
  }, [routeCtx?.selectedDates, routeCtx?.selectedTarget]);

  useEffect(() => {
    if (routeCtx?.clickedRouteId) {
      scrollTo(routeCtx.clickedRouteId);
    }
  }, [routeCtx?.clickedRouteId]);

  function filterEmptyDays() {
    const result: RefObject<HTMLDivElement | null>[] = [];
    try {
      Object.keys(routesByTimes).map((date) => {
        const ref = refs.current.get(date);
        if (ref !== undefined && routesByTimes[date]?.[0]) result.push(ref);
      });
      // eslint-disable-next-line
    } catch (_) {}
    return result;
  }

  useEffect(() => {
    const allRefs = [topRef, ...filterEmptyDays(), bottomRef];

    const observer = new IntersectionObserver(
      (entries) => {
        if (!containerRef.current) return;
        const rContainer = containerRef.current?.getBoundingClientRect();
        let topEntryIndex: number | null = null;

        function findTopVisibleIndex() {
          for (let i = allRefs.length - 2; i >= 0; i--) {
            const el = allRefs[i].current;
            if (!el) continue;

            const r = el.getBoundingClientRect();
            if (r.y - rContainer.y < 0) {
              return i;
            }
          }

          return null;
        }

        let counter = 0;
        entries.forEach((entry) => {
          const rect = entry.boundingClientRect;
          const targetIndex = allRefs.findIndex((section) => section.current === entry.target);
          if (entry.isIntersecting) {
            if (rect.top < 0 && targetIndex !== -1) {
              if (counter === 0) topEntryIndex = targetIndex;
            }
            if (entry.target === bottomRef.current) {
              topEntryIndex = targetIndex - 1;
            }
          } else {
            if (rect.top < 0 && targetIndex !== -1) {
              topEntryIndex = targetIndex + 1;
            }
            if (entry.target === bottomRef.current) {
              topEntryIndex = findTopVisibleIndex() ?? allRefs.length - 2;
            }
          }
          counter++;
        });

        if (topEntryIndex !== null) {
          setActiveSection(allRefs[topEntryIndex].current);
        }
        if (containerRef.current?.scrollTop <= 180) {
          setActiveSection(topRef.current);
          return;
        }
      },
      { root: containerRef.current }
    );

    allRefs.forEach((section) => {
      if (!section.current) return;
      if (section) observer.observe(section.current);
      if (topRef.current && topRef.current.firstElementChild) observer.observe(topRef.current);
    });

    return () => observer.disconnect();
  }, [routeCtx?.routes]);

  useEffect(() => {
    if (routeCtx?.highlightedRoutes?.length) {
      setShowTooltip(false);
    }
  }, [routeCtx?.highlightedRoutes]);

  useEffect(() => {
    sessionStorage.setItem('showRoutesListTooltip', JSON.stringify(showTooltip));
  }, [showTooltip]);

  if (
    !routeCtx ||
    !routeCtx.selectedTarget ||
    !routeCtx.routes ||
    !routeCtx.selectedDates ||
    !routeCtx.selectedDates.from ||
    !routeCtx.selectedDates.to
  ) {
    return null;
  }

  function handleSelectAll() {
    if (selectAllChecked || !routeCtx) {
      plutoCtx?.setSelectedRoutes([]);
    } else {
      plutoCtx?.setSelectedRoutes(convertRouteListToSelected(routeCtx.routes, routeCtx.selectedTarget));
    }
    setSelectAllChecked(!selectAllChecked);
  }

  async function setResult(signal: AbortSignal) {
    if (
      !routeCtx ||
      !routeCtx.selectedTarget ||
      !routeCtx.selectedDates ||
      !routeCtx.selectedDates.from ||
      !routeCtx.selectedDates.to
    )
      return;

    popupCtx?.showLoader();
    routeCtx.setRoutesLoading(true);
    try {
      const toTime = date2micros(routeCtx.selectedDates.to);
      const result = await getRoutesWithPositions(
        routeCtx.selectedTarget.id,
        {
          fromTime: date2micros(routeCtx.selectedDates.from),
          toTime: toTime,
        },
        {
          signal: signal,
        }
      );

      if (signal.aborted) {
        return;
      }

      const resultData = result.data;
      const routes = resultData.routes;
      const positions = resultData.statuses;
      routeCtx.setRoutes(routes ?? []);
      routeCtx.setPositions(positions);
      routeCtx.setActialToTime(new Date(resultData.routeMetadata.toTimeMicros / 1000));
      plutoCtx?.setSelectedRoutes(convertRouteListToSelected(routes, routeCtx.selectedTarget));

      const routeMetadata = resultData.routeMetadata;
      showTooManyRoutesNotification(routeMetadata, routes.length);
    } finally {
      popupCtx?.hideLoader();
      routeCtx.setRoutesLoading(false);
    }
  }

  function showTooManyRoutesNotification(routeMetadata: RouteMetadata, noOfRoutes: number) {
    if (routeMetadata.originalNoOfRoutes == noOfRoutes) {
      return;
    }
    const toTime = routeMetadata.toTimeMicros;
    const toDateTime = fulldate(toTime, t);
    showNotification(
      'info',
      t('routes.tooManyRoutes', {
        toTime: toDateTime,
        limit: routeMetadata.routesLimit,
        noOfRoutes: routeMetadata.originalNoOfRoutes,
        interpolation: { escapeValue: false },
      })
    );
  }

  if (!routeCtx) return null;

  // TODO menjen useEffect-be ?
  const fromDate = routeCtx.selectedDates.from;
  const toDate = routeCtx.selectedDates.to;
  const routeGrouper = new RouteGrouper(i18n, t);
  const routesByTimes = routeGrouper.groupRoutesByTime(routeCtx.routes, fromDate, toDate);

  Object.keys(routesByTimes).map((date) => {
    if (!refs.current.get(date)) refs.current.set(date, createRef<HTMLDivElement>());
  });

  function getDaysWithRoute() {
    return Object.keys(routesByTimes).filter((date) => routesByTimes[date][0]);
  }

  function isOneDay() {
    return getDaysWithRoute().length <= 1;
  }

  function onClickScroll(ref: RefObject<HTMLDivElement | null> | undefined) {
    ref?.current?.scrollIntoView();
    if (ref) {
      setTimeout(() => setActiveSection(ref.current), 200);
    }
  }

  return (
    <div
      className={`route-panel-result ${!isPhoneOrHighScreen() ? 'route-panel-result-desktop' : ''}`}
      data-qa-element-id="routes_result_panel"
    >
      {!isPhoneOrHighScreen() && <RoutesResultHeader />}
      <div className={`route-panel-result-content ${routeCtx.routesLoading ? 'disabled' : ''}`}>
        <div className="route-detail-list" ref={containerRef} data-qa-element-id="route_details_list">
          {!isPhoneOrHighScreen() && <RoutesSummaryHeader />}
          <RoutesSummary ref={topRef} />
          <RouteList
            routesByTimes={routesByTimes}
            showTooltip={showTooltip}
            refs={refs}
            t={t}
            routeCtx={routeCtx}
            registerRef={registerRef}
          />
          <Box ref={bottomRef} sx={{ height: '1px', width: '100%', pointerEvents: 'none' }} />
        </div>
        {!isPhoneOrHighScreen() && !isOneDay() && (
          <div className="route-quick-navigation" data-qa-element-id="routes_quick_date_select_panel">
            <span data-qa-element-id="all_dates_button">
              <QuickNav
                label={t('refuels.aggregatedDataSort')}
                current={activeSection === topRef.current}
                onClickScroll={() => onClickScroll(topRef)}
              />
            </span>
            {getDaysWithRoute().map((date) => (
              <span data-qa-element-id={`quick_date_select_for${stripYear(date)}`}>
                <QuickNav
                  key={date}
                  label={stripYear(date)}
                  current={activeSection === refs.current.get(date)?.current}
                  onClickScroll={() => onClickScroll(refs.current.get(date))}
                />
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Body({ jumpToDateSelect }: { jumpToDateSelect: boolean }) {
  const routesCtx = useContext(ctxRoutes);
  const plutoCtx = useContext(ctxPluto);
  if (jumpToDateSelect) routesCtx?.setTab(TabValue.dateSelect);

  function setSelectedTargetWrapper(target: MinimalTarget | undefined) {
    if (!routesCtx || !target) {
      return;
    }
    routesCtx.setSelectedTarget(target);
    routesCtx.setTab(TabValue.dateSelect);
    routesCtx.setSelectedDates(undefined);
    routesCtx.setRoutes(undefined);
    plutoCtx?.setSelectedRoutes([]);
  }

  function setRoutesWrapper(values: DetailedRoute[] | undefined) {
    if (!routesCtx) return;
    routesCtx.setRoutes(values);
    plutoCtx?.setSelectedRoutes([]);
    routesCtx.setIsBottomSheetOpen(true);
    routesCtx.setIsBottomSheetUp(true);
    routesCtx.setAdjustMap(true);
  }

  function setSelectedDatesWrapper(from: Date, to: Date) {
    if (!routesCtx) return;
    routesCtx.setSelectedDates({ from, to });
  }

  if (!routesCtx) return null;

  switch (routesCtx.tab) {
    case TabValue.targetSelect:
      return (
        <SingleTargetSelector
          setSelectedTarget={setSelectedTargetWrapper}
          selectedTarget={routesCtx.selectedTarget}
          searchText={routesCtx.searchText}
          setSearchText={routesCtx.setSearchText}
        />
      );
    case TabValue.dateSelect:
      return (
        <RoutesDateSelect
          selectedTarget={routesCtx.selectedTarget}
          setRoutes={setRoutesWrapper}
          setSelectedDates={setSelectedDatesWrapper}
        />
      );
    default:
      // ez lehet nem kell ?? nem kell h undefined lehessen ?
      return (
        <SingleTargetSelector
          setSelectedTarget={setSelectedTargetWrapper}
          selectedTarget={routesCtx.selectedTarget}
          searchText={routesCtx.searchText}
          setSearchText={routesCtx.setSearchText}
        />
      );
  }
}

function RoutesSecondary() {
  const routesCtx = useContext(ctxRoutes);
  const plutoCtx = useContext(ctxPluto);

  const today = new Date();
  const from = startOfDay(today);
  const to = endOfDay(today);

  useEffect(() => {
    routesCtx?.setRoutes([]);
  }, [routesCtx?.selectedTarget]);

  if (!routesCtx || !plutoCtx) return;

  async function setSelectedTargetWrapper(target: MinimalTarget | undefined) {
    if (!routesCtx || !plutoCtx || !target) {
      return;
    }

    routesCtx.setSelectedTarget(target);
    if (!routesCtx.selectedDates) routesCtx.setSelectedDates({ from, to });
  }

  if (!routesCtx) return null;
  return (
    <div className={`route-panel-target-selector ${routesCtx.routesLoading ? 'disabled' : ''}`}>
      <>
        <SingleTargetSelector
          setSelectedTarget={setSelectedTargetWrapper}
          selectedTarget={routesCtx.selectedTarget}
          searchText={routesCtx.searchText}
          setSearchText={routesCtx.setSearchText}
        />
      </>
    </div>
  );
}

export default function RoutesPopup() {
  const plutoCtx = useContext(ctxPluto);
  const routesCtx = useContext(ctxRoutes);
  const popupCtx = useContext(ctxPopup);
  const { t } = useTranslation();
  const [showTargetSelector, setShowTargetSelector] = useState(false);
  const [jumpToDateSelect, setJumpToDateSelect] = useState(false);
  const [isRouteSelected, setIsRouteSelected] = useState(false);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const selectedDatesRef = useRef<{ from: Date; to: Date }>(routesCtx?.selectedDates);
  const selectedTargetRef = useRef<MinimalTarget>(routesCtx?.selectedTarget);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    const isOpen = searchParams.get('bottomsheet');
    routesCtx?.setIsBottomSheetOpen(isOpen === 'true');
    return () => routesCtx?.setIsBottomSheetOpen(false);
  }, []);

  useEffect(() => {
    const target: MinimalTarget | undefined = location.state?.target;
    if (!target) return;

    function filterDuplicate(routes: DetailedRoute[] | undefined) {
      if (!routes) return routes;
      const routesSorted = routes.sort((a, b) => a.startPosition.timeMicros - b.startPosition.timeMicros);

      let previous: number = -1;
      const routesFiltered = routesSorted.filter((r) => {
        if (r.startPosition.timeMicros <= previous) return false;
        previous = r.startPosition.timeMicros;
        return true;
      });

      return routesFiltered;
    }

    async function setTargetRoutes() {
      if (!routesCtx || !target) return;

      const today = new Date();
      const from = startOfDay(today);
      const to = endOfDay(today);
      routesCtx?.setSelectedDates({ from, to });

      popupCtx?.showLoader('circular');
      try {
        routesCtx.setSelectedTarget(target);
        const result = await getTargetRoutes(target.id, {
          fromTime: from.getTime() * 1000,
          toTime: to.getTime() * 1000,
        });
        const count = result.data.length;
        if (count === 0) {
          routesCtx?.setIsBottomSheetOpen(false);
          setShowTargetSelector(true);
          setJumpToDateSelect(true);
        } else {
          routesCtx.setRoutes(filterDuplicate(result.data));
          routesCtx?.setIsBottomSheetOpen(true);
        }
      } catch (error) {
        rethrowIfNotClientException(error);
      } finally {
        popupCtx?.hideLoader();
      }
    }

    routesCtx?.setSelectedTarget(target);
    plutoCtx?.setSelectedRoutes([]);
    setTargetRoutes();
    routesCtx?.setIsBottomSheetUp(true);
  }, []);

  useEffect(() => {
    plutoCtx?.setMode('route'); // show selectedTargets on the map
  }, [plutoCtx]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('bottomsheet', routesCtx?.isBottomSheetOpen ? 'true' : 'false');
    setSearchParams(params, {
      preventScrollReset: true,
    });

    if (routesCtx?.isBottomSheetOpen) {
      setShowTargetSelector(false);
    }
  }, [routesCtx?.isBottomSheetOpen]);

  useEffect(() => {
    if (
      !selectedDatesRef ||
      routesCtx?.selectedDates !== selectedDatesRef.current ||
      !selectedTargetRef ||
      routesCtx?.selectedTarget !== selectedTargetRef.current
    ) {
      routesCtx?.setHighlightedRoutes([]);
      selectedDatesRef.current = routesCtx?.selectedDates;
      selectedTargetRef.current = routesCtx?.selectedTarget;
    }

    if (routesCtx?.routes !== undefined) {
      setIsRouteSelected(true);
    } else {
      setIsRouteSelected(false);
    }
  }, [routesCtx?.routes]);

  if (!routesCtx) return;

  return isPhoneOrHighScreen() ? (
    <>
      <TargetSelectorOpenerButton
        setOnClicked={() => {
          setShowTargetSelector(true);
          routesCtx.setIsBottomSheetOpen(false);
        }}
        isVehicleOrRouteSelected={isRouteSelected}
        title={t('mainMenu.routes')}
      />
      {showTargetSelector && (
        <Popup
          popupTitle={t('mainMenu.routes')}
          header={<RoutesHeader />}
          body={<Body jumpToDateSelect={jumpToDateSelect} />}
          footer={undefined}
          isFullScr={true}
          leftText={true}
          closePopup={async () => {
            setShowTargetSelector(false);
            if (routesCtx.routes && routesCtx.routes.length !== 0) {
              routesCtx.setIsBottomSheetOpen(true);
              routesCtx.setAdjustMap(true);
            }
          }}
        />
      )}
      {routesCtx.isBottomSheetOpen && (
        <BottomSheet
          setClosed={() => routesCtx.setIsBottomSheetUp(false)}
          header={<RoutesResultHeader />}
          isBottomSheetUp={routesCtx.isBottomSheetUp}
          setBottomSheetUp={routesCtx.setIsBottomSheetUp}
          routeTab={true}
        >
          <RoutesList topRef={summaryRef} />
        </BottomSheet>
      )}
    </>
  ) : (
    <>
      <RoutesSecondary />
      {routesCtx.routes && <RoutesList topRef={summaryRef} />}
    </>
  );
}
